import { Box, CircularProgress, Divider, List, ListItem, ListItemText, Typography, useTheme, Theme, Chip } from "@mui/material";
import { IAircraft, IAircraftCountResult } from "@vmosolutions/oc-interfaces";
import FlightIcon from "@mui/icons-material/Flight";
import { useEffect, useState } from "react";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";

interface IAircraftOverviewTabProps {
  aircraft: IAircraft | null;
}

const listItemStyles = (theme: Theme): object => ({
  py: 0.5,
  "& .MuiListItemText-primary": {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
  "& .MuiListItemText-secondary": {
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
});

export const AircraftOverviewTab = ({ aircraft }: IAircraftOverviewTabProps): JSX.Element => {
  const theme = useTheme();
  const [airTime, setAirTime] = useState<number | null>(null);
  const [cycles, setCycles] = useState<number | null>(null);
  const [countersLoading, setCountersLoading] = useState(false);
  const [fetchedAircraftId, setFetchedAircraftId] = useState<string | null>(null);

  useEffect(() => {
    const aircraftId = aircraft?._id as string | undefined;
    if (!aircraftId || aircraftId === fetchedAircraftId) {
      return undefined;
    }

    let cancelled = false;
    const fetchCounts = async (): Promise<void> => {
      setCountersLoading(true);
      try {
        const now = new Date().toISOString();
        const response = await AmplifyAPIWrapper.Get<Record<string, IAircraftCountResult>>(
          `/aircraft/counts?aircraftIds=${aircraftId}&datetime=${now}`,
        );
        if (!cancelled && response) {
          const result = response[aircraftId];
          setAirTime(result?.airTime != null ? result.airTime : null);
          setCycles(result?.cycles != null ? result.cycles : null);
        }
      } catch (err) {
        console.error("Failed to fetch aircraft counts", err);
        setAirTime(null);
        setCycles(null);
      } finally {
        if (!cancelled) {
          setCountersLoading(false);
          setFetchedAircraftId(aircraftId);
        }
      }
    };

    void fetchCounts();
    return () => {
      cancelled = true;
    };
  }, [aircraft?._id, fetchedAircraftId]);

  if (!aircraft) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No aircraft data available.
        </Typography>
      </Box>
    );
  }

  const {
    registration,
    serialNumber,
    aircraftType,
    fleetNumber,
    isActive,
    foreignRef,
  } = aircraft;

  return (
    <Box sx={{ overflow: "auto", height: "100%" }}>
      {/* Aircraft Identity Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1.5,
          gap: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <FlightIcon sx={{ fontSize: 28, color: theme.palette.secondary.main }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {registration}
          </Typography>
          {fleetNumber && (
            <Typography variant="caption" color="text.secondary">
              Fleet #{fleetNumber}
            </Typography>
          )}
        </Box>
        <Box sx={{ ml: "auto" }}>
          <Chip
            label={isActive ? "Active" : "Inactive"}
            size="small"
            color={isActive ? "success" : "default"}
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Aircraft Details */}
      <List dense disablePadding>
        <ListItem sx={listItemStyles(theme)}>
          <ListItemText primary="Registration" secondary={registration || "—"} />
        </ListItem>
        <Divider component="li" />

        <ListItem sx={listItemStyles(theme)}>
          <ListItemText primary="Serial Number" secondary={serialNumber || "—"} />
        </ListItem>
        <Divider component="li" />

        <ListItem sx={listItemStyles(theme)}>
          <ListItemText primary="Aircraft Type" secondary={aircraftType?.title || aircraftType?.codeICAO || "—"} />
        </ListItem>
        <Divider component="li" />

        <ListItem sx={listItemStyles(theme)}>
          <ListItemText primary="ICAO Code" secondary={aircraftType?.codeICAO || "—"} />
        </ListItem>
        <Divider component="li" />

        <ListItem sx={listItemStyles(theme)}>
          <ListItemText primary="IATA Code" secondary={aircraftType?.codeIATA || "—"} />
        </ListItem>
        <Divider component="li" />

        <ListItem sx={listItemStyles(theme)}>
          <ListItemText
            primary="Current Hours"
            secondary={
              countersLoading ? (
                <CircularProgress size={14} />
              ) : airTime != null ? (
                `${airTime} hrs`
              ) : (
                "—"
              )
            }
          />
        </ListItem>
        <Divider component="li" />

        <ListItem sx={listItemStyles(theme)}>
          <ListItemText
            primary="Current Cycles"
            secondary={
              countersLoading ? <CircularProgress size={14} /> : cycles != null ? `${cycles}` : "—"
            }
          />
        </ListItem>
        <Divider component="li" />

        {fleetNumber && (
          <>
            <ListItem sx={listItemStyles(theme)}>
              <ListItemText primary="Fleet Number" secondary={fleetNumber} />
            </ListItem>
            <Divider component="li" />
          </>
        )}
      </List>

      {/* Foreign System References */}
      {foreignRef && foreignRef.length > 0 && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.secondary }}>
              Foreign System References
            </Typography>
            {foreignRef.map((ref, index) => (
              <Box key={`${ref.system}-${ref.foreignId}-${index}`} sx={{ mb: 0.5 }}>
                <Typography variant="body2">
                  {ref.system}: <strong>{ref.foreignId}</strong>
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};
